<template>
  <div class="customer-service-widget">
    <transition name="work-order-mask">
      <button
        v-if="open"
        class="uplus-work-order-mask"
        type="button"
        :aria-label="$t('关闭在线工单')"
        @click="closePanel"
      />
    </transition>

    <transition name="work-order-panel">
      <section
        v-if="open"
        class="uplus-work-order-panel"
        :class="{ 'uplus-work-order-panel-chat': hasEmail }"
        :aria-label="$t('在线工单')"
      >
        <header class="uplus-work-order-header">
          <span class="uplus-work-order-icon uplus-work-order-icon-desktop" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M21 11.4a8.4 8.4 0 0 1-9 8.4 9.6 9.6 0 0 1-3.5-.7L3 21l1.8-4.5A8.1 8.1 0 0 1 3 11.4 8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.4Z" /></svg>
          </span>
          <span class="uplus-work-order-icon uplus-work-order-icon-mobile" aria-hidden="true">U</span>
          <div class="uplus-work-order-heading">
            <strong class="uplus-work-order-heading-desktop">{{ $t('在线工单') }}</strong>
            <strong class="uplus-work-order-heading-mobile">{{ $t('客服小U') }}</strong>
            <span class="uplus-work-order-heading-desktop"><i />{{ $t('在线客服 · 通常1小时内回复') }}</span>
            <span class="uplus-work-order-heading-mobile"><i />{{ $t('在线 · 已接入你的账户') }}</span>
          </div>
          <button class="uplus-work-order-close" type="button" :aria-label="$t('关闭')" @click="closePanel">
            <svg viewBox="0 0 24 24"><path d="m6 6 12 12M18 6 6 18" /></svg>
          </button>
        </header>

        <form v-if="!hasEmail" class="uplus-work-order-email" @submit.prevent="submitEmail">
          <label for="uplus-work-order-email">留下你的邮箱，回复将直接发送到邮箱</label>
          <input
            id="uplus-work-order-email"
            v-model="draftEmail"
            type="email"
            :maxlength="emailMaxLength"
            autocomplete="email"
            placeholder="请输入你的邮箱地址"
          />
          <button type="submit">
            开始留言
            <span>→</span>
          </button>
        </form>

        <div v-else class="uplus-work-order-chat">
          <div class="uplus-work-order-reply">
            <span>
              <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></svg>
              回复至 <strong v-raw-text="email" />
            </span>
            <button type="button" @click="changeEmail">更换</button>
          </div>

          <div ref="messageBody" class="uplus-work-order-chat-body" @scroll="handleMessageScroll">
            <div class="uplus-work-order-message-list">
              <time class="uplus-work-order-conversation-time">{{ conversationTimeLabel }}</time>

              <div v-if="!messages.length" class="uplus-work-order-welcome">
                <div class="uplus-work-order-message uplus-work-order-message-service">
                  <span class="uplus-work-order-message-avatar" aria-hidden="true">U</span>
                  <p>{{ $t('你好，我是客服小U') }} <span aria-hidden="true">👋</span></p>
                </div>
                <div class="uplus-work-order-message uplus-work-order-message-service">
                  <span class="uplus-work-order-message-avatar" aria-hidden="true">U</span>
                  <p>{{ $t('账户信息已同步，有什么可以帮你？') }}</p>
                </div>
                <div class="uplus-work-order-quick-list">
                  <button
                    v-for="question in quickQuestions"
                    :key="question"
                    type="button"
                    @click="sendQuickQuestion(question)"
                  >
                    {{ $t(question) }}
                  </button>
                </div>
              </div>

              <div
                v-for="item in messages"
                :key="item.id"
                class="uplus-work-order-message"
                :class="`uplus-work-order-message-${item.role}`"
              >
                <span
                  v-if="item.role !== 'user'"
                  class="uplus-work-order-message-avatar"
                  aria-hidden="true"
                >U</span>
                <div class="uplus-work-order-message-stack">
                  <button
                    v-for="(picture, pictureIndex) in item.pictures"
                    :key="`${item.id}-picture-${pictureIndex}`"
                    class="uplus-work-order-image-preview-button"
                    type="button"
                    aria-label="预览图片"
                    @click="previewMessageImage(item, pictureIndex)"
                  >
                    <img class="uplus-work-order-message-image" :src="picture" alt="" />
                  </button>
                  <p v-if="item.content" v-raw-text="item.content" class="uplus-work-order-message-bubble" />
                </div>
              </div>
            </div>
          </div>

          <div v-if="selectedImagePreview" class="uplus-work-order-selected-image">
            <button
              class="uplus-work-order-selected-preview"
              type="button"
              aria-label="预览图片"
              @click="previewImageUrl = selectedImagePreview"
            >
              <img :src="selectedImagePreview" :alt="selectedImageName" />
            </button>
            <button
              class="uplus-work-order-selected-remove"
              type="button"
              aria-label="移除图片"
              @click="clearSelectedImage"
            >
              <svg viewBox="0 0 24 24"><path d="m6 6 12 12M18 6 6 18" /></svg>
            </button>
          </div>

          <div class="uplus-work-order-compose">
            <input
              ref="imageInput"
              class="uplus-work-order-image-input"
              type="file"
              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              @change="handleImageChange"
            />
            <button
              class="uplus-work-order-image-button"
              type="button"
              aria-label="选择图片或拍照"
              :title="selectedImageName || '选择图片或拍照'"
              :data-has-image="selectedImageName ? 'true' : null"
              @click="openImagePicker"
            >
              <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9" r="1.5" /><path d="m5 18 5-5 3 3 2-2 4 4" /></svg>
            </button>
            <input
              v-model="message"
              type="text"
              :maxlength="messageMaxLength"
              :placeholder="$t('输入你的问题...')"
              :aria-label="$t('工单消息内容')"
              @keydown="handleMessageKeydown"
            />
            <button
              class="uplus-work-order-send-button"
              type="button"
              aria-label="发送"
              :disabled="sending || (!message.trim() && !selectedImageFile)"
              @click="sendMessage"
            >
              <i v-if="sending" class="el-icon-loading" />
              <svg v-else viewBox="0 0 24 24"><path d="m22 2-7 20-4-9-9-4 20-7Z" /><path d="M22 2 11 13" /></svg>
            </button>
          </div>
          <small class="uplus-work-order-privacy">{{ $t('客服不会索要密码、验证码或 API 密钥') }}</small>
        </div>
      </section>
    </transition>

    <button
      class="uplus-floating-consult"
      type="button"
      :aria-label="open ? $t('关闭在线工单') : $t('在线工单')"
      :aria-expanded="open ? 'true' : 'false'"
      @click="togglePanel"
    >
      <svg v-if="!open" viewBox="0 0 24 24"><path d="M21 11.4a8.4 8.4 0 0 1-9 8.4 9.6 9.6 0 0 1-3.5-.7L3 21l1.8-4.5A8.1 8.1 0 0 1 3 11.4 8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.4Z" /></svg>
      <svg v-else viewBox="0 0 24 24"><path d="m6 6 12 12M18 6 6 18" /></svg>
    </button>

    <div
      v-if="previewImageUrl"
      class="uplus-work-order-preview-mask"
      role="dialog"
      aria-modal="true"
      @click="previewImageUrl = ''"
    >
      <button
        class="uplus-work-order-preview-close"
        type="button"
        aria-label="关闭预览"
        @click="previewImageUrl = ''"
      >
        <svg viewBox="0 0 24 24"><path d="m6 6 12 12M18 6 6 18" /></svg>
      </button>
      <img :src="previewImageUrl" alt="" @click.stop />
      <a
        class="uplus-work-order-preview-original"
        :href="previewImageUrl"
        target="_blank"
        rel="noopener noreferrer"
        @click.stop
      >{{ $t('查看原图') }}</a>
    </div>
  </div>
</template>

<script>
import {
  closeCustomerSession,
  CUSTOMER_EMAIL_MAX_LENGTH,
  CUSTOMER_IMAGE_MAX_SIZE,
  CUSTOMER_MESSAGE_MAX_LENGTH,
  fetchCustomerMessagePage,
  prepareCustomerSession,
  submitCustomerFeedback,
  uploadCustomerFeedbackImage
} from '@/api/customer-service'
import { rawText } from '@/directives/raw-content'

const EMAIL_STORAGE_KEY = 'uplus_work_order_email'
const UID_STORAGE_KEY = 'uplus_work_order_uid'
const SUPPORT_VISITOR_STORAGE_KEY = 'X-Support-Visitor'
const OPEN_WORK_ORDER_EVENT = 'uplus:open-work-order'
const POLL_INTERVAL = 10000
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png']
const ALLOWED_IMAGE_EXTENSION = /\.(jpe?g|png)$/i

function isValidEmail(value) {
  return value.length > 0 &&
    value.length <= CUSTOMER_EMAIL_MAX_LENGTH &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function readStoredEmail() {
  try {
    const value = (localStorage.getItem(EMAIL_STORAGE_KEY) || '').trim()
    return isValidEmail(value) ? value : ''
  } catch (error) {
    return ''
  }
}

function createUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `uplus-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function getOrCreateUid() {
  const storedUid = localStorage.getItem(UID_STORAGE_KEY)
  if (storedUid && /^[a-zA-Z0-9-]{1,64}$/.test(storedUid)) return storedUid
  const nextUid = createUid()
  localStorage.setItem(UID_STORAGE_KEY, nextUid)
  return nextUid
}

function hasSameMessage(messages, message) {
  return messages.some(item => item.id === message.id)
}

function mergeLatestMessages(currentMessages, nextMessages) {
  if (!nextMessages.length) return currentMessages
  if (!currentMessages.length) return nextMessages
  const newMessages = nextMessages.filter(item => !hasSameMessage(currentMessages, item))
  return newMessages.length ? currentMessages.concat(newMessages) : currentMessages
}

function prependOlderMessages(currentMessages, olderMessages) {
  const newMessages = olderMessages.filter(item => !hasSameMessage(currentMessages, item))
  return newMessages.length ? newMessages.concat(currentMessages) : currentMessages
}

export default {
  name: 'CustomerServiceWidget',
  directives: { rawText },
  data() {
    const email = readStoredEmail()
    return {
      open: false,
      email,
      draftEmail: email,
      uid: getOrCreateUid(),
      token: localStorage.getItem('token') || '',
      session: null,
      message: '',
      messages: [],
      sending: false,
      pageNum: 1,
      totalPage: 1,
      lastTime: '',
      firstTime: '',
      hasMore: false,
      loadingMore: false,
      exhaustedHistory: false,
      shouldScrollToBottom: true,
      keepScrollPosition: false,
      previousScrollHeight: 0,
      pollTimer: null,
      refreshPending: false,
      selectedImageFile: null,
      selectedImageName: '',
      selectedImagePreview: '',
      previewImageUrl: '',
      quickQuestions: ['我的 API 状态', '提现多久到账', '点卡怎么抵扣', '策略亏损怎么办'],
      emailMaxLength: CUSTOMER_EMAIL_MAX_LENGTH,
      messageMaxLength: CUSTOMER_MESSAGE_MAX_LENGTH
    }
  },
  computed: {
    hasEmail() {
      return this.email.trim().length > 0
    },
    feedbackUid() {
      return this.token ? '' : `${this.uid}&&${this.email.trim()}`
    },
    conversationTimeLabel() {
      const rawTime = this.messages.length ? this.messages[0].rawTime : ''
      const parsed = rawTime ? new Date(String(rawTime).replace(/-/g, '/')) : new Date()
      const date = Number.isNaN(parsed.getTime()) ? new Date() : parsed
      const now = new Date()
      const isToday = date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() && date.getDate() === now.getDate()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const dateLabel = isToday
        ? this.$t('今天')
        : `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} `
      return `${dateLabel}${hours}:${minutes}`
    }
  },
  created() {
    this.$root.$on('open-customer-service', this.openPanel)
    window.addEventListener(OPEN_WORK_ORDER_EVENT, this.openPanel)
  },
  beforeDestroy() {
    this.$root.$off('open-customer-service', this.openPanel)
    window.removeEventListener(OPEN_WORK_ORDER_EVENT, this.openPanel)
    this.stopPolling()
    this.clearSelectedImage()
    if (this.session && this.session.id) closeCustomerSession(this.session).catch(() => {})
  },
  methods: {
    togglePanel() {
      if (this.open) this.closePanel()
      else this.openPanel()
    },
    async openPanel() {
      if (this.open) return
      this.open = true
      if (this.hasEmail) {
        await this.initializeConversation()
      }
    },
    closePanel() {
      this.open = false
      this.stopPolling()
      if (this.session && this.session.id) closeCustomerSession(this.session).catch(() => {})
      this.session = null
    },
    async submitEmail() {
      const nextEmail = this.draftEmail.trim()
      if (!isValidEmail(nextEmail)) return
      localStorage.setItem(EMAIL_STORAGE_KEY, nextEmail)
      this.exhaustedHistory = false
      this.shouldScrollToBottom = true
      this.messages = []
      this.pageNum = 1
      this.totalPage = 1
      this.lastTime = ''
      this.firstTime = ''
      this.hasMore = false
      this.email = nextEmail
      await this.$nextTick()
      await this.initializeConversation()
    },
    changeEmail() {
      localStorage.removeItem(EMAIL_STORAGE_KEY)
      this.stopPolling()
      if (this.session && this.session.id) closeCustomerSession(this.session).catch(() => {})
      this.session = null
      this.exhaustedHistory = false
      this.shouldScrollToBottom = true
      this.draftEmail = ''
      this.clearSelectedImage()
      this.messages = []
      this.pageNum = 1
      this.totalPage = 1
      this.lastTime = ''
      this.firstTime = ''
      this.hasMore = false
      this.email = ''
    },
    async initializeConversation() {
      try {
        this.token = localStorage.getItem('token') || ''
        if (this.token) this.session = await prepareCustomerSession(this.feedbackUid)
        else this.session = null
        await this.loadInitialMessages()
        this.startPolling()
      } catch (error) {
        this.$message.error((error && error.message) || this.$t('客服消息加载失败'))
      }
    },
    async loadInitialMessages() {
      this.pageNum = 1
      this.lastTime = ''
      this.firstTime = ''
      const page = await fetchCustomerMessagePage({
        uid: this.feedbackUid,
        pageNum: this.pageNum,
        headers: this.getSupportVisitorHeaders()
      })
      this.syncSupportVisitorId(page.visitorId)
      this.messages = page.messages
      this.totalPage = page.totalPage
      this.hasMore = this.messages.length > 0 && (page.hasMore || page.totalPage > 1)
      this.shouldScrollToBottom = true
      this.restoreMessageScroll()
      this.$nextTick(() => this.ensureScrollableHistory())
    },
    startPolling() {
      this.stopPolling()
      if (!this.open || !this.hasEmail) return
      this.pollTimer = window.setInterval(() => {
        if (!this.open || (!this.token && !this.feedbackUid) || this.loadingMore || this.sending) return
        this.refreshMessages()
      }, POLL_INTERVAL)
    },
    stopPolling() {
      if (this.pollTimer) window.clearInterval(this.pollTimer)
      this.pollTimer = null
    },
    async refreshMessages() {
      if (!this.open || !this.hasEmail) return
      if (this.refreshPending) return
      this.refreshPending = true
      try {
        const node = this.$refs.messageBody
        const nearBottom = !node || node.scrollHeight - node.scrollTop - node.clientHeight < 80
        this.pageNum = 1
        this.firstTime = ''
        this.lastTime = this.messages.length
          ? this.messages[this.messages.length - 1].rawTime
          : ''
        const page = await fetchCustomerMessagePage({
          uid: this.feedbackUid,
          pageNum: this.pageNum,
          lastTime: this.lastTime,
          firstTime: '',
          headers: this.getSupportVisitorHeaders()
        })
        this.syncSupportVisitorId(page.visitorId)
        this.shouldScrollToBottom = nearBottom || this.messages.length === 0
        const currentMessages = this.messages
        const nextMessages = mergeLatestMessages(currentMessages, page.messages)
        this.messages = nextMessages
        if (this.messages.length) {
          this.lastTime = this.messages[this.messages.length - 1].rawTime
        }
        this.restoreMessageScroll()
      } catch (error) {
        // 定时刷新失败时保留当前聊天内容，不打断用户输入。
      } finally {
        this.refreshPending = false
      }
    },
    async loadMoreMessages() {
      if (this.loadingMore || !this.hasMore || !this.messages.length) return
      const node = this.$refs.messageBody
      if (!node) return

      this.previousScrollHeight = node.scrollHeight
      this.keepScrollPosition = true
      this.shouldScrollToBottom = false
      this.loadingMore = true
      try {
        this.pageNum = 1
        this.firstTime = this.messages[0].rawTime || ''
        this.lastTime = ''
        const page = await fetchCustomerMessagePage({
          uid: this.feedbackUid,
          pageNum: this.pageNum,
          lastTime: '',
          firstTime: this.firstTime,
          headers: this.getSupportVisitorHeaders()
        })
        this.syncSupportVisitorId(page.visitorId)
        const currentMessages = this.messages
        const nextMessages = prependOlderMessages(currentMessages, page.messages)
        const addedOlderMessages = nextMessages !== currentMessages

        if (addedOlderMessages) {
          this.messages = nextMessages
        }
        if (!addedOlderMessages) {
          this.keepScrollPosition = false
          this.hasMore = false
        } else {
          this.hasMore = page.hasMore
        }
        this.restoreMessageScroll()
        this.$nextTick(() => this.ensureScrollableHistory())
      } catch (error) {
        this.keepScrollPosition = false
      } finally {
        this.loadingMore = false
      }
    },
    handleMessageScroll() {
      const node = this.$refs.messageBody
      if (!node || node.scrollTop > 8 || node.scrollHeight <= node.clientHeight + 16) return
      this.loadMoreMessages()
    },
    ensureScrollableHistory() {
      const node = this.$refs.messageBody
      if (!node || !this.hasMore || this.loadingMore) return
      if (node.scrollHeight <= node.clientHeight + 16) this.loadMoreMessages()
    },
    restoreMessageScroll() {
      this.$nextTick(() => {
        const node = this.$refs.messageBody
        if (!node) return
        if (this.keepScrollPosition) {
          node.scrollTop = node.scrollHeight - this.previousScrollHeight
          this.keepScrollPosition = false
          return
        }
        if (this.shouldScrollToBottom) node.scrollTop = node.scrollHeight
      })
    },
    async sendMessage() {
      const content = this.message.trim()
      const imageFile = this.selectedImageFile
      if ((!content && !imageFile) || this.sending) return

      this.shouldScrollToBottom = true
      this.sending = true
      try {
        let picture = ''
        if (imageFile) picture = await uploadCustomerFeedbackImage(imageFile)
        const submitContent = picture && !content ? 'pic_info' : content
        await submitCustomerFeedback({
          content: submitContent,
          picture,
          uid: this.feedbackUid,
          session: this.session,
          headers: this.getSupportVisitorHeaders()
        })
        this.firstTime = ''
        this.lastTime = this.messages.length
          ? this.messages[this.messages.length - 1].rawTime
          : ''
        this.message = ''
        this.clearSelectedImage()
        await this.refreshMessages()
      } catch (error) {
        this.$message.error((error && error.message) || this.$t('消息发送失败'))
      } finally {
        this.sending = false
      }
    },
    async sendQuickQuestion(question) {
      if (!question || this.sending) return
      this.message = question
      await this.$nextTick()
      await this.sendMessage()
    },
    handleMessageKeydown(event) {
      if (event.isComposing || event.keyCode === 229 || event.key !== 'Enter') return
      event.preventDefault()
      this.sendMessage()
    },
    openImagePicker() {
      if (!this.$refs.imageInput) return
      this.$refs.imageInput.value = ''
      this.$refs.imageInput.click()
    },
    previewMessageImage(item, pictureIndex) {
      const originals = item && Array.isArray(item.originalPictures) ? item.originalPictures : []
      const thumbnails = item && Array.isArray(item.pictures) ? item.pictures : []
      this.previewImageUrl = originals[pictureIndex] || thumbnails[pictureIndex] || ''
    },
    handleImageChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      if (file.size > CUSTOMER_IMAGE_MAX_SIZE) {
        event.target.value = ''
        this.clearSelectedImage()
        this.$message.warning(this.$t('图片大小不能超过12MB'))
        return
      }
      const allowedType = !file.type || ALLOWED_IMAGE_TYPES.includes(file.type.toLowerCase())
      if (!ALLOWED_IMAGE_EXTENSION.test(file.name) || !allowedType) {
        event.target.value = ''
        this.clearSelectedImage()
        this.$message.warning(this.$t('只支持 JPG、JPEG 或 PNG 图片'))
        return
      }
      this.clearSelectedImage()
      const previewUrl = URL.createObjectURL(file)
      this.selectedImageFile = file
      this.selectedImageName = file.name
      this.selectedImagePreview = previewUrl
    },
    getSupportVisitorHeaders() {
      if (this.token) return {}
      const visitorId = localStorage.getItem(SUPPORT_VISITOR_STORAGE_KEY)
      return visitorId ? { 'X-Support-Visitor': visitorId } : {}
    },
    syncSupportVisitorId(visitorId) {
      if (this.token || !visitorId) return
      if (localStorage.getItem(SUPPORT_VISITOR_STORAGE_KEY) !== visitorId) {
        localStorage.setItem(SUPPORT_VISITOR_STORAGE_KEY, visitorId)
      }
    },
    clearSelectedImage() {
      if (this.selectedImagePreview) URL.revokeObjectURL(this.selectedImagePreview)
      this.selectedImageFile = null
      this.selectedImageName = ''
      this.selectedImagePreview = ''
      if (this.$refs.imageInput) this.$refs.imageInput.value = ''
    }
  }
}
</script>

<style scoped>
.customer-service-widget,
.customer-service-widget * {
  box-sizing: border-box;
}

.uplus-floating-consult {
  position: fixed;
  right: 28px;
  bottom: 28px;
  z-index: 13101;
  display: inline-flex;
  width: 56px;
  height: 56px;
  padding: 0;
  align-items: center;
  justify-content: center;
  color: #fff;
  border: 0;
  border-radius: 50%;
  background: #2454df;
  box-shadow: 0 16px 30px -12px rgba(36, 84, 223, .62);
  transition: transform .2s ease, box-shadow .2s ease, background-color .2s ease;
}

.uplus-floating-consult:hover {
  background: #1d4ed8;
  box-shadow: 0 20px 34px -12px rgba(36, 84, 223, .7);
  transform: translateY(-2px);
}

.uplus-floating-consult:active {
  transform: translateY(0);
}

.uplus-floating-consult svg,
.uplus-work-order-header svg,
.uplus-work-order-reply svg,
.uplus-work-order-compose svg,
.uplus-work-order-selected-image svg,
.uplus-work-order-preview-mask svg {
  width: 25px;
  height: 25px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.uplus-work-order-mask,
.uplus-work-order-icon-mobile,
.uplus-work-order-heading-mobile,
.uplus-work-order-conversation-time,
.uplus-work-order-message-avatar,
.uplus-work-order-quick-list,
.uplus-work-order-privacy {
  display: none;
}

.uplus-work-order-welcome {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.uplus-work-order-welcome .uplus-work-order-message:nth-child(2) {
  display: none;
}

.uplus-work-order-panel {
  position: fixed;
  right: 42px;
  bottom: 102px;
  z-index: 13100;
  width: min(360px, calc(100vw - 32px));
  overflow: hidden;
  border: 1px solid rgba(222, 228, 239, .95);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 26px 60px -24px rgba(10, 23, 51, .45);
  font-family: "Noto Sans SC", Manrope, "Microsoft YaHei", "PingFang SC", sans-serif;
}

.uplus-work-order-panel-chat {
  display: flex;
  height: 660px;
  flex-direction: column;
}

.uplus-work-order-header {
  display: flex;
  min-height: 70px;
  padding: 16px 16px 15px 18px;
  align-items: center;
  gap: 12px;
  color: #fff;
  background: #102659;
}

.uplus-work-order-icon {
  display: inline-flex;
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: #b9ccff;
  border-radius: 10px;
  background: rgba(255, 255, 255, .1);
}

.uplus-work-order-heading {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 4px;
}

.uplus-work-order-heading strong {
  font-size: 19px;
  font-weight: 800;
  line-height: 1.1;
}

.uplus-work-order-heading span {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #cbd8ff;
  font-size: 12px;
  line-height: 1.2;
  white-space: nowrap;
}

.uplus-work-order-heading i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #17c986;
}

.uplus-work-order-close {
  display: inline-flex;
  width: 32px;
  height: 32px;
  padding: 0;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: #fff;
  border: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, .16);
}

.uplus-work-order-close svg {
  width: 21px;
  height: 21px;
}

.uplus-work-order-email {
  display: flex;
  padding: 20px 18px;
  flex-direction: column;
  gap: 14px;
}

.uplus-work-order-email label {
  color: #14213f;
  font-size: 14px;
  font-weight: 800;
}

.uplus-work-order-email input,
.uplus-work-order-compose input {
  width: 100%;
  color: #25334f;
  border: 1px solid #dce4f2;
  outline: none;
  background: #fff;
  font-family: inherit;
}

.uplus-work-order-email input {
  height: 44px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 14px;
}

.uplus-work-order-email input:focus,
.uplus-work-order-compose input:focus {
  border-color: #9db8ff;
  box-shadow: 0 0 0 3px rgba(36, 84, 223, .1);
}

.uplus-work-order-email button {
  display: inline-flex;
  height: 44px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #fff;
  border: 0;
  border-radius: 9px;
  background: #2454df;
  box-shadow: 0 16px 28px -18px rgba(36, 84, 223, .8);
  font-size: 15px;
  font-weight: 800;
}

.uplus-work-order-chat {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  background: #f5f7fc;
}

.uplus-work-order-reply {
  display: flex;
  height: 50px;
  padding: 0 16px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #e6ebf4;
  background: #f0f3fb;
}

.uplus-work-order-reply span {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 7px;
  color: #46546b;
  font-size: 12px;
}

.uplus-work-order-reply svg {
  width: 15px;
  height: 15px;
  flex: 0 0 auto;
  color: #2454df;
}

.uplus-work-order-reply strong {
  overflow: hidden;
  color: #25334f;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.uplus-work-order-reply button {
  color: #2454df;
  border: 0;
  background: transparent;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.uplus-work-order-chat-body {
  min-height: 0;
  padding: 16px;
  flex: 1;
  overflow-y: auto;
  scrollbar-color: #d6deec transparent;
  scrollbar-width: thin;
}

.uplus-work-order-chat-body::-webkit-scrollbar {
  width: 6px;
}

.uplus-work-order-chat-body::-webkit-scrollbar-track {
  background: transparent;
}

.uplus-work-order-chat-body::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: #d6deec;
}

.uplus-work-order-message-list {
  display: flex;
  min-height: 100%;
  flex-direction: column;
  gap: 12px;
}

.uplus-work-order-message {
  display: flex;
}

.uplus-work-order-message-service {
  justify-content: flex-start;
}

.uplus-work-order-message-user {
  justify-content: flex-end;
}

.uplus-work-order-message-stack {
  display: flex;
  max-width: min(300px, 86%);
  flex-direction: column;
  align-items: flex-start;
}

.uplus-work-order-message-user .uplus-work-order-message-stack {
  align-items: flex-end;
}

.uplus-work-order-message > p,
.uplus-work-order-message-bubble {
  box-sizing: border-box;
  width: max-content;
  margin: 0;
  padding: 8px;
  overflow-wrap: anywhere;
  color: #273651;
  border: 1px solid #e4eaf4;
  border-radius: 11px;
  background: #fff;
  box-shadow: 0 8px 20px -18px rgba(10, 23, 51, .4);
  font-size: 13px;
  line-height: 1.65;
  white-space: pre-wrap;
  word-break: break-word;
}

.uplus-work-order-message > p {
  max-width: min(300px, 86%);
}

.uplus-work-order-message-bubble {
  max-width: 100%;
}

.uplus-work-order-message-service > p,
.uplus-work-order-message-service .uplus-work-order-message-bubble {
  border-bottom-left-radius: 4px;
}

.uplus-work-order-message-user > p,
.uplus-work-order-message-user .uplus-work-order-message-bubble {
  color: #fff;
  border-color: #2454df;
  border-bottom-right-radius: 4px;
  background: #2454df;
}

.uplus-work-order-image-preview-button {
  display: block;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: zoom-in;
}

.uplus-work-order-message-image {
  display: block;
  max-width: 180px;
  max-height: 180px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  box-shadow: none;
  object-fit: cover;
}

.uplus-work-order-image-preview-button + .uplus-work-order-message-bubble {
  margin-top: 8px;
}

.uplus-work-order-image-preview-button + .uplus-work-order-image-preview-button {
  margin-top: 8px;
}

.uplus-work-order-selected-image {
  display: flex;
  padding: 10px 12px 8px;
  align-items: center;
  gap: 8px;
  border-top: 1px solid #e6ebf4;
  background: #fff;
}

.uplus-work-order-selected-preview {
  display: inline-flex;
  width: 54px;
  height: 54px;
  padding: 0;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  cursor: zoom-in;
}

.uplus-work-order-selected-preview img {
  width: 54px;
  height: 54px;
  border: 1px solid #dce4f2;
  border-radius: 8px;
  background: #f7f9ff;
  object-fit: contain;
}

.uplus-work-order-selected-remove {
  display: inline-flex;
  width: 22px;
  height: 22px;
  padding: 0;
  align-items: center;
  justify-content: center;
  color: #fff;
  border: 0;
  border-radius: 50%;
  background: #7b879c;
}

.uplus-work-order-selected-remove svg {
  width: 14px;
  height: 14px;
}

.uplus-work-order-preview-mask {
  position: fixed;
  inset: 0;
  z-index: 13200;
  display: flex;
  padding: 24px;
  align-items: center;
  justify-content: center;
  background: rgba(7, 18, 43, .78);
}

.uplus-work-order-preview-mask img {
  max-width: min(92vw, 920px);
  max-height: 88vh;
  border-radius: 12px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, .34);
  object-fit: contain;
}

.uplus-work-order-preview-original {
  position: fixed;
  bottom: 22px;
  left: 50%;
  min-width: 92px;
  padding: 9px 16px;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, .34);
  border-radius: 999px;
  background: rgba(14, 26, 53, .72);
  font-size: 13px;
  font-weight: 700;
  text-align: center;
  text-decoration: none;
  transform: translateX(-50%);
}

.uplus-work-order-preview-close {
  position: fixed;
  top: 22px;
  right: 22px;
  display: inline-flex;
  width: 38px;
  height: 38px;
  padding: 0;
  align-items: center;
  justify-content: center;
  color: #fff;
  border: 0;
  border-radius: 10px;
  background: rgba(255, 255, 255, .16);
}

.uplus-work-order-preview-close svg {
  width: 22px;
  height: 22px;
}

.uplus-work-order-compose {
  display: flex;
  padding: 12px;
  align-items: center;
  gap: 9px;
  border-top: 1px solid #e6ebf4;
  background: #fff;
}

.uplus-work-order-compose input:not(.uplus-work-order-image-input) {
  height: 42px;
  padding: 0 13px;
  border-radius: 10px;
  font-size: 13px;
}

.uplus-work-order-image-input {
  display: none;
}

.uplus-work-order-compose button {
  display: inline-flex;
  width: 42px;
  height: 42px;
  padding: 0;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: #fff;
  border: 0;
  border-radius: 11px;
  background: #2454df;
}

.uplus-work-order-compose .uplus-work-order-image-button {
  color: #5d6c86;
  border: 1px solid #dce4f2;
  background: #fff;
}

.uplus-work-order-compose .uplus-work-order-image-button[data-has-image="true"] {
  color: #2454df;
  border-color: #9db8ff;
  background: #f0f4ff;
}

.uplus-work-order-compose .uplus-work-order-send-button {
  color: #fff;
  border-color: transparent;
  background: #2454df;
}

.uplus-work-order-compose button:disabled {
  cursor: not-allowed;
  opacity: .55;
}

.work-order-panel-enter-active,
.work-order-panel-leave-active {
  transition: opacity .18s ease, transform .18s ease;
}

.work-order-panel-enter,
.work-order-panel-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(.985);
}

@media (max-width: 760px) {
  .uplus-work-order-mask {
    position: fixed;
    inset: 0;
    z-index: 13099;
    display: block;
    width: 100%;
    height: 100%;
    padding: 0;
    border: 0;
    background: rgba(17, 24, 39, .42);
  }

  .uplus-floating-consult {
    right: 18px;
    bottom: calc(50px + env(safe-area-inset-bottom));
    z-index: 140;
    width: 52px;
    height: 52px;
  }

  .uplus-floating-consult[aria-expanded="true"] {
    display: none;
  }

  .uplus-work-order-panel {
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    max-width: none;
    border: 0;
    border-radius: 20px 20px 0 0;
    box-shadow: none;
  }

  .uplus-work-order-panel-chat {
    height: 78dvh;
    max-height: calc(100dvh - 18px);
  }

  .uplus-work-order-header {
    min-height: 68px;
    padding: 13px 16px;
    gap: 10px;
    background: #2161e8;
  }

  .uplus-work-order-icon-desktop,
  .uplus-work-order-heading-desktop,
  .uplus-work-order-reply,
  .uplus-work-order-image-button {
    display: none !important;
  }

  .uplus-work-order-icon-mobile {
    display: inline-flex;
    width: 36px;
    height: 36px;
    color: #fff;
    border-radius: 50%;
    background: rgba(255, 255, 255, .18);
    font-size: 13px;
    font-weight: 800;
  }

  .uplus-work-order-heading-mobile {
    display: flex;
  }

  .uplus-work-order-heading strong.uplus-work-order-heading-mobile {
    font-size: 16px;
    font-weight: 800;
    line-height: 20px;
  }

  .uplus-work-order-heading span.uplus-work-order-heading-mobile {
    color: rgba(255, 255, 255, .92);
    font-size: 11px;
    line-height: 14px;
  }

  .uplus-work-order-heading i {
    width: 6px;
    height: 6px;
    background: #54e49d;
  }

  .uplus-work-order-close {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: rgba(255, 255, 255, .15);
  }

  .uplus-work-order-close svg {
    width: 17px;
    height: 17px;
  }

  .uplus-work-order-chat {
    background: #f7f9fc;
  }

  .uplus-work-order-email {
    padding-bottom: calc(50px + env(safe-area-inset-bottom));
  }

  .uplus-work-order-chat-body {
    padding: 16px 16px 12px;
  }

  .uplus-work-order-message-list {
    gap: 10px;
  }

  .uplus-work-order-conversation-time {
    display: block;
    margin: 0 0 2px;
    color: #a7b0c1;
    font-size: 10px;
    font-style: normal;
    line-height: 14px;
    text-align: center;
  }

  .uplus-work-order-welcome {
    gap: 10px;
  }

  .uplus-work-order-welcome .uplus-work-order-message:nth-child(2) {
    display: flex;
  }

  .uplus-work-order-message {
    align-items: flex-end;
    gap: 8px;
  }

  .uplus-work-order-message-avatar {
    display: inline-flex;
    width: 26px;
    height: 26px;
    flex: 0 0 26px;
    align-items: center;
    justify-content: center;
    color: #fff;
    border-radius: 50%;
    background: #2c6ef2;
    font-size: 10px;
    font-weight: 800;
  }

  .uplus-work-order-message-stack {
    max-width: calc(100% - 42px);
  }

  .uplus-work-order-message > p,
  .uplus-work-order-message-bubble {
    padding: 9px 11px;
    border-color: #e3e8f1;
    border-radius: 12px;
    box-shadow: 0 4px 10px rgba(26, 46, 82, .04);
    font-size: 13px;
    line-height: 20px;
  }

  .uplus-work-order-message > p {
    max-width: calc(100% - 42px);
  }

  .uplus-work-order-message-service > p,
  .uplus-work-order-message-service .uplus-work-order-message-bubble {
    border-bottom-left-radius: 4px;
  }

  .uplus-work-order-message-user {
    padding-left: 34px;
  }

  .uplus-work-order-quick-list {
    display: flex;
    padding-left: 34px;
    flex-wrap: wrap;
    gap: 7px;
  }

  .uplus-work-order-quick-list button {
    min-height: 29px;
    padding: 4px 11px;
    color: #1f67ef;
    border: 1px solid #d3e0f6;
    border-radius: 15px;
    background: #fff;
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
  }

  .uplus-work-order-selected-image {
    padding: 8px 14px;
  }

  .uplus-work-order-compose {
    padding: 10px 14px 3px;
    gap: 8px;
    background: #fff;
  }

  .uplus-work-order-compose input:not(.uplus-work-order-image-input) {
    height: 42px;
    padding: 0 14px;
    border-color: #dce3ef;
    border-radius: 999px;
    background: #f7f9fc;
    font-size: 12px;
  }

  .uplus-work-order-compose input:not(.uplus-work-order-image-input):focus {
    border-color: #b9cdf6;
    box-shadow: none;
  }

  .uplus-work-order-compose .uplus-work-order-send-button {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #2161e8;
  }

  .uplus-work-order-compose .uplus-work-order-send-button svg {
    width: 18px;
    height: 18px;
  }

  .uplus-work-order-compose button:disabled {
    cursor: default;
    opacity: 1;
  }

  .uplus-work-order-privacy {
    display: block;
    padding: 3px 12px calc(50px + env(safe-area-inset-bottom));
    color: #a5afc0;
    background: #fff;
    font-size: 9px;
    font-weight: 400;
    line-height: 14px;
    text-align: center;
  }

  .work-order-panel-enter-active,
  .work-order-panel-leave-active {
    transition: opacity .24s ease, transform .28s cubic-bezier(.22, 1, .36, 1);
  }

  .work-order-panel-enter,
  .work-order-panel-leave-to {
    opacity: 0;
    transform: translateY(100%);
  }

  .work-order-mask-enter-active,
  .work-order-mask-leave-active {
    transition: opacity .2s ease;
  }

  .work-order-mask-enter,
  .work-order-mask-leave-to {
    opacity: 0;
  }
}
</style>
